import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@lwt-helix/buttons';
import Modal from '@lwt-helix/modal';
import { Input } from '@lwt-helix/controls';
import { favoriteAgentTerms as favTerms } from '../../constants/agentProductionConstants';
import { buttonTerms } from '../../../constants';
import { useCommonStore, useSearchStore } from '../../../store/store';
import { showToast, toastTypes } from '../../../common/helpers/toast';
import { getSelectedAgentIds } from '../../../common/components/table/agGrid/crossViewSelection';

const NewAgentListModal = ({
    isModalOpen,
    onClose,
    setShowAddToListModal,
    setShowNewListModal,
    setToastProps,
}) => {
    const { agentLists, createAgentsList } = useCommonStore((state) => ({
        agentLists: state.agentLists,
        createAgentsList: state.createAgentsList,
    }));
    const { mlsId, selectedAgentIds } = useSearchStore((state) => ({
        mlsId: state[state.selectedModule]?.search?.mlsId,
        selectedAgentIds: getSelectedAgentIds(state) ?? [],
    }));
    const [listName, setListName] = useState('');
    const [isSaveBtnDisable, setIsSaveBtnDisabled] = useState(true);
    const NEW_LIST_MAX_LENGTH = 55;
    
    const handleClose = () => {
        onClose();
        setListName('');
        setIsSaveBtnDisabled(true);
    };

    const onListNameChange = (v) => {
        const listName = v.target.value;
        setListName(listName);
        setIsSaveBtnDisabled(!(listName.trim()?.length));
    };

    const handleSave = async () => {
        const trimmedName = listName.trim();
        const isDuplicatedName = agentLists
            ?.filter((l) => l?.mlsId === mlsId)
            .some((l) => l?.name?.toLocaleLowerCase() === trimmedName.toLocaleLowerCase() ||
                trimmedName.toLocaleLowerCase() === favTerms.reservedListName.toLocaleLowerCase());

        if (isDuplicatedName) {
            showToast(toastTypes.error, favTerms.duplicatedName, setToastProps);
            return;
        }

        try {
            const response = await createAgentsList(mlsId, trimmedName, selectedAgentIds);
            const hasError = !response || response.error;
            if (hasError) {
                showToast(toastTypes.error, favTerms.unableToCreateList, setToastProps);
            } else {
                setListName('');
                setIsSaveBtnDisabled(true);
                showToast(
                    toastTypes.success,
                    favTerms.agentListCreated(trimmedName),
                    setToastProps
                );
            }
        } catch (err) {
            showToast(toastTypes.error, favTerms.unableToCreateList, setToastProps);
        }
        setShowAddToListModal(false);
        setShowNewListModal(false);
    };

    const children = (
        <>
            <span className="font-weight-bold">{favTerms.newList}</span>
            <div className="d-flex align-items-center justify-space-between">
                <Input
                    onChange={onListNameChange}
                    placeholder={favTerms.typeNewList}
                    value={listName}
                    maxLength={NEW_LIST_MAX_LENGTH}
                />
            </div>
            <small>{favTerms.chooseNameNewList}</small>
        </>
    );

    const modalTitle = (
        <>
            <p className="mb-0">{favTerms.createList}</p>
            <p className="modal-subtitle">{favTerms.selectedAgentsSavedNewList}</p>
        </>
    );

    const buttons = (
        <>
            <Button
                dataLwtId="cancel-create-new-agent-list"
                color="secondary"
                onClick={handleClose}
            >
                {buttonTerms.cancel}
            </Button>
            <Button
                    className="d-flex align-items-center justify-content-center"
                    color="primary"
                    dataLwtId="save-create-new-agent-list"
                    disabled={isSaveBtnDisable}
                    onClick={handleSave}
                >
                    {buttonTerms.save}
                </Button>
        </>
    );

    return (
        <Modal
            buttons={buttons}
            show={isModalOpen}
            title={modalTitle}
            onClose={handleClose}
            children={children}
        />
    );
};

NewAgentListModal.propTypes = {
    isModalOpen: PropTypes.bool,
    onClose: PropTypes.func,
    setShowAddToListModal: PropTypes.func.isRequired,
    setShowNewListModal: PropTypes.func,
    setToastProps: PropTypes.func,
};

export default NewAgentListModal;
